/**
 * Parser OFX (Open Financial Exchange) pour importer des relevés bancaires
 * Format standard supporté par la plupart des banques françaises et internationales
 */

export interface OFXTransaction {
  date: string;
  amount: number;
  label: string;
  type: string;
  checkNumber?: string;
  memo?: string;
}

export interface OFXParseResult {
  success: boolean;
  transactions: OFXTransaction[];
  accountId?: string;
  accountNumber?: string;
  currency?: string;
  startDate?: string;
  endDate?: string;
  ledgerBalance?: number;
  availableBalance?: number;
  balanceDate?: string;
  error?: string;
}

/**
 * Parse un fichier OFX et extrait les transactions
 */
export async function parseOFXFile(file: File): Promise<OFXParseResult> {
  try {
    const content = await file.text();

    console.log('[OFX Parser] File size:', file.size, 'bytes');
    console.log('[OFX Parser] First 200 chars:', content.substring(0, 200));

    // Vérifier que c'est bien un fichier OFX
    if (!content.includes('OFX') && !content.includes('OFXHEADER')) {
      return {
        success: false,
        transactions: [],
        error: 'Le fichier ne semble pas être au format OFX. Assurez-vous d\'exporter au format OFX/QFX depuis votre banque.'
      };
    }

    // Nettoyer le contenu (supprimer les headers SGML si présents)
    let ofxContent = content;
    const ofxStartIndex = content.indexOf('<OFX>');
    if (ofxStartIndex !== -1) {
      ofxContent = content.substring(ofxStartIndex);
    } else {
      return {
        success: false,
        transactions: [],
        error: 'Balise <OFX> introuvable. Le fichier semble corrompu.'
      };
    }

    console.log('[OFX Parser] Content after headers removed, length:', ofxContent.length);

    // Détecter si c'est du SGML (ancien format) ou du XML (nouveau format)
    // SGML n'a pas de balises fermantes pour les éléments (pas de </STMTTRN>, </TRNTYPE>, etc.)
    const hasSGMLStructure = !ofxContent.includes('</STMTTRN>') && !ofxContent.includes('</TRNTYPE>');
    console.log('[OFX Parser] Format detected:', hasSGMLStructure ? 'SGML' : 'XML');

    // Si c'est du SGML, essayer de le parser directement avec regex (plus fiable)
    if (hasSGMLStructure) {
      console.log('[OFX Parser] Using SGML parser (regex-based)...');
      return parseSGMLOFX(ofxContent, file.name);
    }

    // Parser le XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(ofxContent, 'text/xml');

    // Vérifier les erreurs de parsing
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      console.error('[OFX Parser] XML parsing error:', parserError.textContent);
      console.log('[OFX Parser] Falling back to SGML parser...');

      // Fallback: essayer le parser SGML si le XML échoue
      return parseSGMLOFX(ofxContent, file.name);
    }

    // Extraire les informations du compte
    const accountId = getTagValue(xmlDoc, 'ACCTID');
    const accountNumber = accountId;
    const currency = getTagValue(xmlDoc, 'CURDEF') || 'EUR';

    // Extraire les dates de période
    const startDate = getTagValue(xmlDoc, 'DTSTART');
    const endDate = getTagValue(xmlDoc, 'DTEND');

    // Extraire les soldes
    let ledgerBalance: number | undefined;
    let availableBalance: number | undefined;
    let balanceDate: string | undefined;

    const ledgerBal = xmlDoc.querySelector('LEDGERBAL');
    if (ledgerBal) {
      const balAmt = getChildTagValue(ledgerBal, 'BALAMT');
      const dtAsOf = getChildTagValue(ledgerBal, 'DTASOF');
      if (balAmt) ledgerBalance = parseFloat(balAmt);
      if (dtAsOf) balanceDate = parseOFXDate(dtAsOf);
    }

    const availBal = xmlDoc.querySelector('AVAILBAL');
    if (availBal) {
      const balAmt = getChildTagValue(availBal, 'BALAMT');
      if (balAmt) availableBalance = parseFloat(balAmt);
    }

    // Extraire les transactions
    const transactions: OFXTransaction[] = [];
    const stmttrn = xmlDoc.querySelectorAll('STMTTRN');

    console.log('[OFX Parser] Found', stmttrn.length, 'transactions');

    if (stmttrn.length === 0) {
      return {
        success: false,
        transactions: [],
        error: 'Aucune transaction trouvée dans le fichier OFX. Vérifiez que le fichier contient bien des transactions.'
      };
    }

    stmttrn.forEach((trn, index) => {
      const trntype = getChildTagValue(trn, 'TRNTYPE');
      const dtposted = getChildTagValue(trn, 'DTPOSTED');
      const trnamt = getChildTagValue(trn, 'TRNAMT');
      const name = getChildTagValue(trn, 'NAME');
      const memo = getChildTagValue(trn, 'MEMO');
      const checknum = getChildTagValue(trn, 'CHECKNUM');

      if (dtposted && trnamt) {
        // Convertir la date OFX (YYYYMMDD ou YYYYMMDDHHMMSS) en format ISO
        const date = parseOFXDate(dtposted);

        // Construire le libellé
        let label = name || memo || 'Transaction';
        if (memo && name && memo !== name) {
          label = `${name} - ${memo}`;
        }

        transactions.push({
          date,
          amount: parseFloat(trnamt),
          label: label.trim(),
          type: trntype || 'OTHER',
          checkNumber: checknum,
          memo: memo || undefined
        });
      } else {
        console.warn('[OFX Parser] Skipping transaction', index, '- missing date or amount');
      }
    });

    // Trier par date (plus récent en premier)
    transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    console.log('[OFX Parser] Successfully parsed', transactions.length, 'transactions');

    return {
      success: true,
      transactions,
      accountId,
      accountNumber,
      currency,
      startDate: startDate ? parseOFXDate(startDate) : undefined,
      endDate: endDate ? parseOFXDate(endDate) : undefined,
      ledgerBalance,
      availableBalance,
      balanceDate
    };

  } catch (error) {
    console.error('Error parsing OFX file:', error);
    return {
      success: false,
      transactions: [],
      error: error instanceof Error ? error.message : 'Erreur inconnue lors du parsing'
    };
  }
}

/**
 * Parse un fichier OFX au format SGML (ancien format sans balises fermantes)
 */
function parseSGMLOFX(sgmlContent: string, fileName: string): OFXParseResult {
  try {
    // Extraire les informations du compte avec regex
    const accountIdMatch = sgmlContent.match(/<ACCTID>([^\n<]+)/);
    const accountId = accountIdMatch ? accountIdMatch[1].trim() : undefined;

    const currencyMatch = sgmlContent.match(/<CURDEF>([^\n<]+)/);
    const currency = currencyMatch ? currencyMatch[1].trim() : 'EUR';

    const startDateMatch = sgmlContent.match(/<DTSTART>([^\n<]+)/);
    const startDate = startDateMatch ? parseOFXDate(startDateMatch[1].trim()) : undefined;

    const endDateMatch = sgmlContent.match(/<DTEND>([^\n<]+)/);
    const endDate = endDateMatch ? parseOFXDate(endDateMatch[1].trim()) : undefined;

    // Extraire les soldes
    let ledgerBalance: number | undefined;
    let availableBalance: number | undefined;
    let balanceDate: string | undefined;

    const ledgerBalMatch = sgmlContent.match(/<LEDGERBAL>([\s\S]*?)(?=<AVAILBAL>|<\/LEDGERBAL>|$)/);
    if (ledgerBalMatch) {
      const balAmtMatch = ledgerBalMatch[1].match(/<BALAMT>([^\n<]+)/);
      const dtAsOfMatch = ledgerBalMatch[1].match(/<DTASOF>([^\n<]+)/);
      if (balAmtMatch) ledgerBalance = parseFloat(balAmtMatch[1].trim());
      if (dtAsOfMatch) balanceDate = parseOFXDate(dtAsOfMatch[1].trim());
    }

    const availBalMatch = sgmlContent.match(/<AVAILBAL>([\s\S]*?)(?=<\/AVAILBAL>|$)/);
    if (availBalMatch) {
      const balAmtMatch = availBalMatch[1].match(/<BALAMT>([^\n<]+)/);
      if (balAmtMatch) availableBalance = parseFloat(balAmtMatch[1].trim());
    }

    console.log('[SGML Parser] Account ID:', accountId, 'Currency:', currency);

    // Extraire les transactions avec regex
    const transactions: OFXTransaction[] = [];

    // Trouver tous les blocs STMTTRN - essayer plusieurs patterns
    let stmttrnMatches: RegExpMatchArray[] = [];

    // Pattern 1: Avec balise fermante optionnelle
    const pattern1 = /<STMTTRN>([\s\S]*?)(?=<STMTTRN>|<\/STMTTRN>|<\/BANKTRANLIST>|<LEDGERBAL>|$)/g;
    stmttrnMatches = [...sgmlContent.matchAll(pattern1)];

    // Si aucune correspondance, essayer un pattern plus simple
    if (stmttrnMatches.length === 0) {
      const pattern2 = /<STMTTRN>([\s\S]*?)(?=<STMTTRN>|$)/g;
      stmttrnMatches = [...sgmlContent.matchAll(pattern2)];
    }

    console.log('[SGML Parser] Found', stmttrnMatches.length, 'transaction blocks');

    stmttrnMatches.forEach((match, index) => {
      const trnBlock = match[1];

      // Extraire les champs de la transaction - plusieurs patterns possibles
      const trnTypeMatch = trnBlock.match(/<TRNTYPE>([^\n<]+)/);
      const dtPostedMatch = trnBlock.match(/<DTPOSTED>([^\n<]+)/);
      const trnAmtMatch = trnBlock.match(/<TRNAMT>([^\n<]+)/);
      const nameMatch = trnBlock.match(/<NAME>([^\n<]+)/);
      const memoMatch = trnBlock.match(/<MEMO>([^\n<]+)/);
      const checkNumMatch = trnBlock.match(/<CHECKNUM>([^\n<]+)/);
      const fitidMatch = trnBlock.match(/<FITID>([^\n<]+)/);

      // Debug: afficher le bloc si pas de date ou montant
      if (!dtPostedMatch || !trnAmtMatch) {
        console.warn('[SGML Parser] Transaction', index, 'block content:', trnBlock.substring(0, 200));
        console.warn('[SGML Parser] Missing:', !dtPostedMatch ? 'DTPOSTED' : '', !trnAmtMatch ? 'TRNAMT' : '');
      }

      if (dtPostedMatch && trnAmtMatch) {
        try {
          const date = parseOFXDate(dtPostedMatch[1].trim());
          const amount = parseFloat(trnAmtMatch[1].trim());
          const name = nameMatch ? nameMatch[1].trim() : '';
          const memo = memoMatch ? memoMatch[1].trim() : '';
          const trnType = trnTypeMatch ? trnTypeMatch[1].trim() : 'OTHER';
          const checkNum = checkNumMatch ? checkNumMatch[1].trim() : undefined;

          // Construire le libellé
          let label = name || memo || 'Transaction';
          if (memo && name && memo !== name) {
            label = `${name} - ${memo}`;
          }

          transactions.push({
            date,
            amount,
            label: label.trim(),
            type: trnType,
            checkNumber: checkNum,
            memo: memo || undefined
          });
        } catch (error) {
          console.error('[SGML Parser] Error parsing transaction', index, ':', error);
        }
      } else {
        console.warn('[SGML Parser] Skipping transaction', index, '- missing required fields');
      }
    });

    if (transactions.length === 0) {
      return {
        success: false,
        transactions: [],
        error: 'Aucune transaction trouvée dans le fichier OFX. Vérifiez que le fichier contient bien des transactions.'
      };
    }

    // Trier par date (plus récent en premier)
    transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    console.log('[SGML Parser] Successfully parsed', transactions.length, 'transactions');

    return {
      success: true,
      transactions,
      accountId,
      accountNumber: accountId,
      currency,
      startDate,
      endDate,
      ledgerBalance,
      availableBalance,
      balanceDate
    };

  } catch (error) {
    console.error('[SGML Parser] Error:', error);
    return {
      success: false,
      transactions: [],
      error: error instanceof Error ? error.message : 'Erreur lors du parsing SGML'
    };
  }
}

/**
 * Récupère la valeur d'un tag dans le document XML
 */
function getTagValue(doc: Document, tagName: string): string | null {
  const element = doc.querySelector(tagName);
  return element ? element.textContent?.trim() || null : null;
}

/**
 * Récupère la valeur d'un tag enfant
 */
function getChildTagValue(parent: Element, tagName: string): string | null {
  const element = parent.querySelector(tagName);
  return element ? element.textContent?.trim() || null : null;
}

/**
 * Convertit une date OFX (YYYYMMDD[HHMMSS][.XXX][TIMEZONE]) en format ISO
 */
function parseOFXDate(ofxDate: string): string {
  // Nettoyer la date (enlever les espaces et caractères spéciaux en fin)
  const cleanDate = ofxDate.trim().split(/[[\s.:-]/)[0];

  // Vérifier que la date a au moins 8 caractères
  if (cleanDate.length < 8) {
    console.warn('[OFX Parser] Invalid date format:', ofxDate);
    return new Date().toISOString().split('T')[0]; // Date du jour par défaut
  }

  // Extraire les parties de la date
  const year = cleanDate.substring(0, 4);
  const month = cleanDate.substring(4, 6);
  const day = cleanDate.substring(6, 8);

  // Construire la date ISO (YYYY-MM-DD)
  return `${year}-${month}-${day}`;
}

/**
 * Détecte le type de transaction bancaire
 */
export function detectTransactionType(type: string): 'CREDIT' | 'DEBIT' | 'OTHER' {
  const creditTypes = ['CREDIT', 'DEP', 'DEPOSIT', 'XFER', 'INT', 'DIV'];
  const debitTypes = ['DEBIT', 'CHECK', 'PAYMENT', 'CASH', 'ATM', 'FEE', 'SRVCHG'];

  if (creditTypes.includes(type.toUpperCase())) {
    return 'CREDIT';
  } else if (debitTypes.includes(type.toUpperCase())) {
    return 'DEBIT';
  }
  return 'OTHER';
}

/**
 * Valide si un fichier est bien au format OFX
 */
export async function isOFXFile(file: File): Promise<boolean> {
  try {
    const firstChunk = await file.slice(0, 1000).text();
    return firstChunk.includes('OFX') || firstChunk.includes('OFXHEADER');
  } catch {
    return false;
  }
}
